import { OcmPoi } from '../../../../stores/types/ocm.interface';
import { FinderFilters, FinderOrigin, FinderResult, FinderStatus } from '../types';
import { getOcmConnectionTypeIds } from './connectors';
import { getDistanceKm, isValidCoordinate } from './geo';
import { filterByNetworks, getNetworkId } from './networks';
import {
  getConnectionLevel,
  getFinderStatus,
  isCustomerOnly,
  isOfflineStatus,
  isOnlineStatus,
} from './status';

export const RESULT_LIMIT = 10;
// Widen the search until RESULT_LIMIT chargers survive filtering
export const SEARCH_RADII_KM = [25, 75, 200];
export const MAX_SEARCH_RADIUS_KM = SEARCH_RADII_KM[SEARCH_RADII_KM.length - 1];

const toFinderResult = (
  poi: OcmPoi,
  origin: FinderOrigin,
  filters: FinderFilters,
  connectionTypeIds: Set<number>,
): FinderResult | null => {
  const latitude = poi.AddressInfo?.Latitude;
  const longitude = poi.AddressInfo?.Longitude;
  if (!isValidCoordinate(latitude, longitude) || isCustomerOnly(poi)) return null;

  const status = getFinderStatus(poi.StatusType);
  const isAllowedStatus =
    isOnlineStatus(status) || (status === FinderStatus.UNKNOWN && filters.includeUnknownStatus);
  if (!isAllowedStatus) return null;

  const connections = (poi.Connections ?? []).filter(
    (connection) =>
      (!connectionTypeIds.size || connectionTypeIds.has(connection.ConnectionTypeID ?? -1)) &&
      (filters.level === undefined || getConnectionLevel(connection) === filters.level) &&
      !isOfflineStatus(getFinderStatus(connection.StatusType)),
  );
  if (!connections.length) return null;

  return {
    poi,
    latitude: latitude as number,
    longitude: longitude as number,
    distanceKm: getDistanceKm(
      origin.latitude,
      origin.longitude,
      latitude as number,
      longitude as number,
    ),
    status,
    connections,
    maxPowerKw: Math.max(0, ...connections.map((connection) => connection.PowerKW ?? 0)),
    networkId: getNetworkId(poi),
  };
};

/**
 * Keeps sites that are online (or unknown, if allowed), not customer-only, and still have
 * at least one matching connector that is not marked offline. Sorted by distance from origin.
 * The network filter is not applied here so the result can also list the networks nearby.
 */
export const filterPois = (
  pois: OcmPoi[],
  origin: FinderOrigin,
  filters: FinderFilters,
): FinderResult[] => {
  const connectionTypeIds = new Set(getOcmConnectionTypeIds(filters.connectorTypes));
  return pois
    .map((poi) => toFinderResult(poi, origin, filters, connectionTypeIds))
    .filter((result): result is FinderResult => result !== null)
    .sort((a, b) => a.distanceKm - b.distanceKm);
};

interface FindNearbyCandidatesOptions {
  onRadiusStart?: (radiusKm: number) => void;
  /** Stops widening once the caller no longer needs the result */
  isCancelled?: () => boolean;
}

/**
 * Fetches each radius in SEARCH_RADII_KM in turn until enough sites match every filter
 * (including networks), and returns all sites matching the non-network filters at that radius.
 */
export const findNearbyCandidates = async (
  fetchPoisWithinKm: (radiusKm: number) => Promise<OcmPoi[]>,
  origin: FinderOrigin,
  filters: FinderFilters,
  { onRadiusStart, isCancelled }: FindNearbyCandidatesOptions = {},
): Promise<FinderResult[]> => {
  let candidates: FinderResult[] = [];
  // Each radius depends on the previous one's result, so these requests are sequential
  for (const radiusKm of SEARCH_RADII_KM) {
    if (isCancelled?.()) break;
    onRadiusStart?.(radiusKm);
    candidates = filterPois(await fetchPoisWithinKm(radiusKm), origin, filters);
    if (filterByNetworks(candidates, filters.networkIds).length >= RESULT_LIMIT) break;
  }
  return candidates;
};
